import React, { useState, useEffect } from 'react';
import { useUI } from '../../../components/common/UIMessageProvider';
import { 
  ALL_SIDEBAR_MENU_ITEMS,
  updateSidebarVisibility,
  getUserSidebarVisibility,
  UserData
} from '../../../firebase/usersFirestoreService';
import { 
  FaEye,
  FaEyeSlash,
  FaCheck,
  FaTimes,
  FaSave,
  FaUndo,
  FaToggleOn,
  FaToggleOff
} from '../../../utils/iconUtils';

interface SidebarVisibilityManagerProps {
  user: UserData;
  onUpdate?: () => void;
}

const SidebarVisibilityManager: React.FC<SidebarVisibilityManagerProps> = ({ user, onUpdate }) => {
  const ui = useUI();
  const [loading, setLoading] = useState(false);
  const [currentVisibility, setCurrentVisibility] = useState<Record<string, boolean>>({});
  const [editedVisibility, setEditedVisibility] = useState<Record<string, boolean>>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Load current sidebar visibility
  useEffect(() => {
    loadSidebarVisibility();
  }, [user.uid]);

  const loadSidebarVisibility = async () => {
    setLoading(true);
    try {
      const result = await getUserSidebarVisibility(user.uid);
      if (result.success) {
        const visibility = result.data;
        
        // Initialize with default visibility for all items
        const allItemsVisibility = ALL_SIDEBAR_MENU_ITEMS.reduce((acc, item) => {
          // Default to true if not specified
          acc[item.path] = visibility[item.path] ?? true;
          return acc;
        }, {} as Record<string, boolean>);
        
        setCurrentVisibility(allItemsVisibility);
        setEditedVisibility(allItemsVisibility);
      }
    } catch (error) {
      ui.showMessage('Error loading sidebar visibility', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const toggleItemVisibility = (path: string) => {
    setEditedVisibility(prev => {
      const newVisibility = {
        ...prev,
        [path]: !prev[path]
      };
      
      // Check if there are changes
      const changesExist = Object.keys(newVisibility).some(
        key => newVisibility[key] !== currentVisibility[key]
      );
      setHasChanges(changesExist);
      
      return newVisibility;
    });
  };

  const toggleAll = (visible: boolean) => {
    setEditedVisibility(prev => {
      const newVisibility = { ...prev };
      ALL_SIDEBAR_MENU_ITEMS.forEach(item => {
        newVisibility[item.path] = visible;
      });
      
      setHasChanges(true);
      return newVisibility;
    });
  };

// In SidebarVisibilityManager.tsx, update the saveChanges function:
const saveChanges = async () => {
  const ok = await ui.askConfirm({
    title: 'Update Sidebar Visibility',
    message: `Are you sure you want to update sidebar items for ${user.email}?`
  });
  
  if (!ok) return;
  
  setLoading(true);
  try {
    const result = await updateSidebarVisibility(user.uid, editedVisibility);
    if (result.success) {
      setCurrentVisibility(editedVisibility);
      setHasChanges(false);
      ui.showMessage('Sidebar visibility updated successfully');
      
      // IMPORTANT: Notify the user to refresh their profile
 ui.showMessage('Sidebar visibility updated. Changes will take effect after refresh.');

      onUpdate?.();
    } else {
      ui.showMessage(`Error: ${result.error}`, { type: 'error' });
    }
  } catch (error: any) {
    ui.showMessage('Error updating sidebar visibility', { type: 'error' });
  } finally {
    setLoading(false);
  }
};
  const resetChanges = () => {
    setEditedVisibility(currentVisibility);
    setHasChanges(false);
  };

  if (loading && Object.keys(currentVisibility).length === 0) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const visibleCount = Object.values(editedVisibility).filter(v => v).length;
  const totalCount = ALL_SIDEBAR_MENU_ITEMS.length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Sidebar Item Visibility</h3>
          <p className="text-sm text-gray-600">
            Control which sidebar items {user.email} can see
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">
            {visibleCount}/{totalCount} visible
          </span>
          {hasChanges && (
            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
              Unsaved Changes
            </span>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex space-x-2">
        <button
          onClick={() => toggleAll(true)}
          className="flex items-center space-x-2 px-3 py-2 text-sm border border-green-300 rounded-lg hover:bg-green-50 transition-colors"
        >
          <FaEye className="h-4 w-4 text-green-600" />
          <span>Show All</span>
        </button>
        <button
          onClick={() => toggleAll(false)}
          className="flex items-center space-x-2 px-3 py-2 text-sm border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
        >
          <FaEyeSlash className="h-4 w-4 text-red-600" />
          <span>Hide All</span>
        </button>
      </div>

      {/* Items Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {ALL_SIDEBAR_MENU_ITEMS.map(item => {
          const isVisible = editedVisibility[item.path] ?? true;
          const isChanged = editedVisibility[item.path] !== currentVisibility[item.path];
          
          return (
            <div
              key={item.path}
              className={`border rounded-lg p-3 transition-all ${
                isChanged
                  ? 'border-yellow-400 bg-yellow-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <h4 className="font-medium text-gray-900">{item.label}</h4>
                    {isChanged && (
                      <span className="px-1.5 py-0.5 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                        Changed
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1 truncate">{item.path}</p>
                  {item.requiredSection && (
                    <p className="text-xs text-blue-600 mt-1">
                      Requires: {item.requiredSection}
                    </p>
                  )}
                </div>
                
                <button
                  onClick={() => toggleItemVisibility(item.path)}
                  className={`ml-3 p-2 rounded-lg transition-colors ${
                    isVisible
                      ? 'bg-green-100 hover:bg-green-200 text-green-700'
                      : 'bg-red-100 hover:bg-red-200 text-red-700'
                  }`}
                  title={isVisible ? 'Hide this item' : 'Show this item'}
                >
                  {isVisible ? (
                    <FaToggleOn className="h-5 w-5" />
                  ) : (
                    <FaToggleOff className="h-5 w-5" />
                  )}
                </button>
              </div>
              
              <div className="flex items-center mt-3">
                <span className={`text-sm ${isVisible ? 'text-green-600' : 'text-red-600'}`}>
                  {isVisible ? 'Visible in sidebar' : 'Hidden from sidebar'}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Actions */}
      {hasChanges && (
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            onClick={resetChanges}
            className="flex items-center space-x-2 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={loading}
          >
            <FaUndo className="h-4 w-4" />
            <span>Reset Changes</span>
          </button>
          <button
            onClick={saveChanges}
            className="flex items-center space-x-2 px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
          >
            <FaSave className="h-4 w-4" />
            <span>{loading ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default SidebarVisibilityManager;